<script setup lang="ts">
/**
 * DocsView — 1:1 порт docs/mockups/public-docs.html (inbox-166).
 *
 * Spec: docs/specs/2026-05-19-public-behavior.md §31 «Документация».
 * STYLE_NOTES: hero compact (72px не 88px — это docs-hero, не landing),
 *   section 80px, container 1180px.
 *
 * Layout: 3-col grid (sidebar 260px / content / toc 220px). Sidebar
 * sticky top:80px, TOC sticky. На <=1100px TOC прячется, на <=800px
 * sidebar тоже схлопывается → 1 col mobile.
 *
 * Caddy: с этого момента /docs полностью обслуживается Vue Router'ом
 * (Swagger UI убран из @backend matcher per брифу).
 */
import { ref } from 'vue';
import AppPublic from '@/layouts/AppPublic.vue';
import { usePageHead, productJsonLd } from '@/composables/usePageHead';

usePageHead({
  title: 'API Documentation — link-platform',
  description:
    'REST API для управления ссылками, аналитикой, кампаниями. OpenAPI 3.0, webhook events, SDK для Python/JS/PHP/1С.',
  path: '/docs',
  type: 'website',
  jsonLd: productJsonLd(
    'Документация link-platform',
    'API-референс, гайды, SDK, рецепты для маркетологов и разработчиков.',
  ),
});

// ── Sidebar navigation (6 групп per мокапу) ──────────────────────
interface NavGroup {
  title: string;
  items: { label: string; href: string; active?: boolean }[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    title: 'Старт',
    items: [
      { label: 'Быстрый старт', href: '#quick-start', active: true },
      { label: 'Концепции', href: '#concepts' },
      { label: 'Установка SDK', href: '#sdk-install' },
      { label: 'Аутентификация', href: '#auth' },
    ],
  },
  {
    title: 'Ссылки',
    items: [
      { label: 'Создание ссылок', href: '#links-create' },
      { label: 'Smart Routing', href: '#smart-routing' },
      { label: 'A/B-тесты', href: '#ab-tests' },
      { label: 'Кастомные домены', href: '#custom-domains' },
      { label: 'QR и Deep Links', href: '#qr-deeplinks' },
    ],
  },
  {
    title: 'Аналитика',
    items: [
      { label: 'События клика', href: '#click-events' },
      { label: 'Конверсии и postback', href: '#conversions' },
      { label: 'Антифрод', href: '#antifraud' },
      { label: 'Экспорт данных', href: '#export' },
    ],
  },
  {
    title: 'Интеграции',
    items: [
      { label: 'Yandex Метрика', href: '#int-yandex' },
      { label: 'Bitrix24', href: '#int-bitrix' },
      { label: 'AmoCRM', href: '#int-amo' },
      { label: 'Webhooks', href: '#webhooks' },
    ],
  },
  {
    title: 'API Reference',
    items: [
      { label: 'REST API v1', href: '#api-v1' },
      { label: 'Ошибки и коды', href: '#errors' },
      { label: 'Rate limits', href: '#rate-limits' },
      { label: 'Changelog', href: '#changelog' },
    ],
  },
  {
    title: 'SDK',
    items: [
      { label: 'Python', href: '#sdk-py' },
      { label: 'JavaScript / Node', href: '#sdk-js' },
      { label: 'PHP', href: '#sdk-php' },
      { label: '1С', href: '#sdk-1c' },
    ],
  },
];

// ── Search ───────────────────────────────────────────────────────
const searchQuery = ref('');

// ── TOC items (внутри-страничные, current page) ──────────────────
const TOC = [
  { id: 'step-1', label: 'Получите API-ключ' },
  { id: 'step-2', label: 'Создайте первую ссылку' },
  { id: 'step-3', label: 'Отслеживайте клики', active: true },
  { id: 'step-4', label: 'Настройте webhook' },
];
</script>

<template>
  <AppPublic>
    <!-- HERO — компактный docs-hero (72px не 88px landing'овых) -->
    <section class="docs-hero">
      <div class="container">
        <div class="docs-hero-content">
          <div class="hero-badge">Документация · API · Гайды</div>
          <h1 class="docs-title">
            Документация <span class="gradient">link-platform</span>
          </h1>
          <p class="docs-sub">
            Гайды, API-референс, рецепты для маркетологов и разработчиков.
            Всё на русском, без воды.
          </p>
          <div class="docs-search">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Найти в документации..."
              aria-label="Поиск по документации"
            />
            <span class="kbd" aria-hidden="true">⌘K</span>
          </div>
        </div>
      </div>
    </section>

    <!-- LAYOUT (sidebar + content + TOC) -->
    <div class="container">
      <div class="docs-layout">
        <!-- Sidebar nav -->
        <aside class="sidebar-nav" aria-label="Разделы документации">
          <div v-for="(group, gi) in NAV_GROUPS" :key="gi" class="nav-group">
            <div class="nav-group-title">{{ group.title }}</div>
            <a
              v-for="item in group.items"
              :key="item.href"
              :href="item.href"
              :class="['nav-item', { active: item.active }]"
            >
              {{ item.label }}
            </a>
          </div>
        </aside>

        <!-- Main content (Quick Start) -->
        <main class="docs-content">
          <div class="breadcrumb">
            <a href="/docs">Документация</a> · <a href="#start">Старт</a> · Быстрый старт
          </div>
          <h1>Быстрый старт</h1>
          <p class="lead">
            Создадим первую ссылку через API за 5 минут. После прохождения вы
            будете уметь генерировать short-link, отслеживать клики и обрабатывать
            конверсии.
          </p>

          <div class="callout note">
            <div class="title">Что нужно</div>
            Аккаунт на link-platform (бесплатный тариф достаточно) и любой HTTP-клиент:
            curl, Postman или ваша CRM.
          </div>

          <h2 id="step-1">Шаг 1 — Получите API-ключ</h2>
          <p>
            После регистрации перейдите в <code>Настройки → API</code> и нажмите
            <strong>«Создать ключ»</strong>. Скопируйте его — больше он показан не будет.
          </p>
          <pre><span class="c"># Сохраните ключ в переменную окружения для удобства</span>
<span class="k">export</span> LP_API_KEY=<span class="s">"lp_live_a8c4f6...xyz"</span></pre>

          <div class="callout warning">
            <div class="title">Безопасность</div>
            Не коммитьте ключи в публичный git. Используйте переменные окружения или Vault.
          </div>

          <h2 id="step-2">Шаг 2 — Создайте первую ссылку</h2>
          <p>Отправьте POST-запрос на <code>/api/v1/links</code>:</p>
          <pre><span class="m">curl</span> -X POST https://api.link-platform.ru/v1/links \
  -H <span class="s">"Authorization: Bearer $LP_API_KEY"</span> \
  -H <span class="s">"Content-Type: application/json"</span> \
  -d <span class="s">'{
    "target_url": "https://example.com/promo",
    "slug": "promo-2026"
  }'</span></pre>

          <p>В ответ получите объект ссылки:</p>
          <pre>{
  <span class="s">"id"</span>: <span class="s">"lnk_a8c4f6"</span>,
  <span class="s">"short_url"</span>: <span class="s">"https://lnk.pl/promo-2026"</span>,
  <span class="s">"target_url"</span>: <span class="s">"https://example.com/promo"</span>,
  <span class="s">"created_at"</span>: <span class="s">"2026-05-19T08:30:12Z"</span>,
  <span class="s">"clicks"</span>: <span class="n">0</span>
}</pre>

          <h2 id="step-3">Шаг 3 — Отслеживайте клики</h2>
          <p>Каждый клик автоматически попадает в аналитику. Получите метрики:</p>
          <pre><span class="m">curl</span> https://api.link-platform.ru/v1/links/lnk_a8c4f6/stats \
  -H <span class="s">"Authorization: Bearer $LP_API_KEY"</span></pre>

          <p>Ответ содержит подробную разбивку по дням, гео, устройствам и источникам:</p>
          <pre>{
  <span class="s">"total_clicks"</span>: <span class="n">1247</span>,
  <span class="s">"unique_visitors"</span>: <span class="n">893</span>,
  <span class="s">"by_country"</span>: { <span class="s">"RU"</span>: <span class="n">1102</span>, <span class="s">"BY"</span>: <span class="n">98</span>, <span class="s">"KZ"</span>: <span class="n">47</span> },
  <span class="s">"by_device"</span>: { <span class="s">"mobile"</span>: <span class="n">812</span>, <span class="s">"desktop"</span>: <span class="n">435</span> }
}</pre>

          <h2 id="step-4">Шаг 4 — Настройте webhook (опционально)</h2>
          <p>Получайте уведомления о каждом клике или конверсии. Создайте подписку:</p>
          <pre><span class="m">curl</span> -X POST https://api.link-platform.ru/v1/webhooks \
  -H <span class="s">"Authorization: Bearer $LP_API_KEY"</span> \
  -d <span class="s">'{
    "url": "https://your-app.ru/lp-webhook",
    "events": ["click", "conversion"]
  }'</span></pre>

          <div class="callout note">
            <div class="title">Что дальше</div>
            <ul style="margin: 6px 0 0; padding-left: 18px;">
              <li><a href="#smart-routing">Smart Routing</a> — разные landing'и для разных аудиторий</li>
              <li><a href="#int-bitrix">Bitrix24 интеграция</a> — клик → лид в CRM за 3 минуты</li>
              <li><a href="#sdk-py">SDK для Python</a> — обёртка над REST с типизацией</li>
            </ul>
          </div>

          <!-- Pager -->
          <nav class="docs-pager" aria-label="Навигация по страницам">
            <a href="#intro" class="pager-card">
              <div class="dir">← Назад</div>
              <div class="ttl">Введение</div>
            </a>
            <a href="#concepts" class="pager-card next">
              <div class="dir">Дальше →</div>
              <div class="ttl">Концепции</div>
            </a>
          </nav>
        </main>

        <!-- TOC -->
        <aside class="toc" aria-label="На странице">
          <div class="toc-title">На странице</div>
          <a
            v-for="item in TOC"
            :key="item.id"
            :href="`#${item.id}`"
            :class="{ active: item.active }"
          >
            {{ item.label }}
          </a>
        </aside>
      </div>
    </div>
  </AppPublic>
</template>

<style scoped>
.container {
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 32px;
}

/* ─── HERO — компактный docs-hero ──────────────────────────────── */
.docs-hero {
  position: relative;
  padding: 72px 0 48px;
  overflow: hidden;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
.docs-hero::before {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse 60% 50% at 30% 20%, rgba(79, 140, 255, 0.12), transparent 70%);
  pointer-events: none;
}
.docs-hero-content {
  position: relative;
  max-width: 760px;
  margin: 0 auto;
  text-align: center;
}
.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  margin-bottom: 22px;
  background: rgba(79, 140, 255, 0.10);
  border: 1px solid rgba(79, 140, 255, 0.25);
  border-radius: 100px;
  font-size: 13px;
  color: #93b9ff;
  font-weight: 500;
}
.hero-badge::before {
  content: '';
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #4f8cff;
  box-shadow: 0 0 8px #4f8cff;
}
h1.docs-title {
  font-size: 44px;
  line-height: 1.1;
  letter-spacing: -0.025em;
  font-weight: 700;
  margin-bottom: 16px;
  color: #e2e8f0;
}
h1.docs-title .gradient {
  background: linear-gradient(135deg, #4f8cff 0%, #7c5cff 60%, #b366ff 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
}
.docs-sub {
  font-size: 17px;
  color: #94a3b8;
  max-width: 580px;
  margin: 0 auto 28px;
  line-height: 1.55;
}
.docs-search {
  max-width: 480px;
  margin: 0 auto;
  position: relative;
}
.docs-search input {
  width: 100%;
  padding: 12px 16px 12px 44px;
  background: #161a23;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  color: #e2e8f0;
  font: inherit;
  font-size: 14.5px;
  outline: 0;
  transition: border 0.15s;
}
.docs-search input::placeholder {
  color: #64748b;
}
.docs-search input:focus {
  border-color: #4f8cff;
}
.docs-search svg {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  width: 17px;
  height: 17px;
  color: #64748b;
}
.docs-search .kbd {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  padding: 2px 7px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 4px;
  font-family: 'SF Mono', ui-monospace, Menlo, monospace;
  font-size: 11px;
  color: #64748b;
}

/* ─── LAYOUT ───────────────────────────────────────────────────── */
.docs-layout {
  display: grid;
  grid-template-columns: 260px 1fr 220px;
  gap: 40px;
  padding: 40px 0 80px;
}

/* ─── SIDEBAR NAV ──────────────────────────────────────────────── */
.sidebar-nav {
  position: sticky;
  top: 80px;
  align-self: start;
  max-height: calc(100vh - 100px);
  overflow-y: auto;
  padding-right: 8px;
}
.nav-group {
  margin-bottom: 22px;
}
.nav-group-title {
  font-size: 11.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #64748b;
  margin-bottom: 10px;
  padding-left: 10px;
}
.nav-item {
  display: block;
  padding: 6px 10px;
  font-size: 13.5px;
  color: #94a3b8;
  border-radius: 6px;
  border-left: 2px solid transparent;
  text-decoration: none;
  transition: all 0.15s;
}
.nav-item:hover {
  background: #161a23;
  color: #e2e8f0;
}
.nav-item.active {
  background: rgba(79, 140, 255, 0.10);
  color: #93b9ff;
  border-left-color: #4f8cff;
  font-weight: 500;
}

/* ─── MAIN CONTENT ─────────────────────────────────────────────── */
.docs-content {
  min-width: 0;
}
.breadcrumb {
  font-size: 12.5px;
  color: #64748b;
  margin-bottom: 14px;
}
.breadcrumb a {
  color: #94a3b8;
  text-decoration: none;
}
.breadcrumb a:hover {
  color: #93b9ff;
}
.docs-content h1 {
  font-size: 36px;
  line-height: 1.15;
  letter-spacing: -0.02em;
  font-weight: 700;
  margin-bottom: 12px;
  color: #e2e8f0;
}
.docs-content .lead {
  font-size: 17px;
  color: #94a3b8;
  margin-bottom: 32px;
  line-height: 1.6;
}
.docs-content :deep(h2) {
  font-size: 24px;
  font-weight: 700;
  letter-spacing: -0.01em;
  margin-top: 40px;
  margin-bottom: 14px;
  scroll-margin-top: 80px;
  color: #e2e8f0;
}
.docs-content h2 {
  font-size: 24px;
  font-weight: 700;
  letter-spacing: -0.01em;
  margin-top: 40px;
  margin-bottom: 14px;
  scroll-margin-top: 80px;
  color: #e2e8f0;
}
.docs-content h3 {
  font-size: 18px;
  font-weight: 600;
  margin-top: 28px;
  margin-bottom: 10px;
  color: #e2e8f0;
}
.docs-content p {
  font-size: 15px;
  color: #cbd5e1;
  margin-bottom: 14px;
  line-height: 1.7;
}
.docs-content ul,
.docs-content ol {
  padding-left: 22px;
  margin-bottom: 16px;
}
.docs-content li {
  font-size: 15px;
  line-height: 1.7;
  margin-bottom: 6px;
  color: #cbd5e1;
}
.docs-content :deep(code),
.docs-content code {
  font-family: 'SF Mono', ui-monospace, Menlo, monospace;
  font-size: 13px;
  padding: 2px 6px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 4px;
  color: #93b9ff;
}
.docs-content pre {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 16px 18px;
  margin-bottom: 18px;
  overflow-x: auto;
  font-size: 13px;
  line-height: 1.6;
  font-family: 'SF Mono', ui-monospace, Menlo, monospace;
  color: #cbd5e1;
}
.docs-content pre code {
  padding: 0;
  background: transparent;
  border: 0;
  color: inherit;
  font-size: inherit;
}
.docs-content pre .k {
  color: #c678dd;
}
.docs-content pre .s {
  color: #98c379;
}
.docs-content pre .c {
  color: #64748b;
  font-style: italic;
}
.docs-content pre .n {
  color: #d19a66;
}
.docs-content pre .m {
  color: #e06c75;
}
.docs-content a {
  color: #93b9ff;
}

/* ─── CALLOUTS ─────────────────────────────────────────────────── */
.callout {
  background: #161a23;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-left: 3px solid #4f8cff;
  border-radius: 0 10px 10px 0;
  padding: 14px 18px;
  margin: 18px 0;
  font-size: 14.5px;
  line-height: 1.6;
  color: #cbd5e1;
}
.callout.note {
  border-left-color: #4f8cff;
}
.callout.warning {
  border-left-color: #f59e0b;
}
.callout.danger {
  border-left-color: #ff6b6b;
}
.callout .title {
  font-weight: 700;
  font-size: 12.5px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #93b9ff;
  margin-bottom: 4px;
}
.callout.warning .title {
  color: #f59e0b;
}
.callout.danger .title {
  color: #ff6b6b;
}

/* ─── TOC ──────────────────────────────────────────────────────── */
.toc {
  position: sticky;
  top: 80px;
  align-self: start;
  font-size: 13px;
}
.toc-title {
  font-size: 11.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #64748b;
  margin-bottom: 10px;
}
.toc a {
  display: block;
  padding: 4px 0 4px 12px;
  color: #94a3b8;
  border-left: 2px solid transparent;
  text-decoration: none;
  transition: all 0.15s;
}
.toc a.active,
.toc a:hover {
  color: #93b9ff;
  border-left-color: #4f8cff;
}

/* ─── PAGER ────────────────────────────────────────────────────── */
.docs-pager {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-top: 48px;
  padding-top: 32px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}
.pager-card {
  padding: 16px 20px;
  background: #161a23;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  transition: all 0.15s;
  text-decoration: none;
}
.pager-card:hover {
  border-color: #4f8cff;
}
.pager-card .dir {
  font-size: 11.5px;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 4px;
}
.pager-card .ttl {
  font-weight: 600;
  color: #e2e8f0;
  font-size: 14.5px;
}
.pager-card.next {
  text-align: right;
}

/* ─── RESPONSIVE ───────────────────────────────────────────────── */
@media (max-width: 1100px) {
  .docs-layout {
    grid-template-columns: 220px 1fr;
  }
  .toc {
    display: none;
  }
}
@media (max-width: 800px) {
  .container {
    padding: 0 24px;
  }
  .docs-hero {
    padding: 48px 0 32px;
  }
  h1.docs-title {
    font-size: 32px;
  }
  .docs-sub {
    font-size: 15.5px;
  }
  .docs-search .kbd {
    display: none;
  }
  .docs-layout {
    grid-template-columns: 1fr;
    padding: 24px 0 60px;
    gap: 24px;
  }
  .sidebar-nav {
    position: relative;
    top: 0;
    max-height: none;
  }
  .docs-content h1 {
    font-size: 28px;
  }
  .docs-content h2 {
    font-size: 20px;
  }
}
</style>
